import type { BuildingGraphFaceSideHandle } from "building-types/buildingGraph/buildingGraphSideHandles";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import {
  composeFilterGraphsAnd,
  instanceGraphHasPredicates,
} from "building-types/buildingGraphFilter/composeFilterGraphs";
import type { RowForSource } from "building-types/buildingGraphFilter/entitySource";
import type { WallPlaneFilterMatches } from "building-types/buildingGraphFilter/wallPlaneFilterMatches";
import type { DocFaceNodeSubFilter, DocWallPlaneFilter } from "building-types/walls2/wallPlaneFilters";
import type { WallFaceRuleEntity } from "building-types/walls2/wallPlaneRules";
import { isWallFaceRule, wallFaceRuleHasConstraint } from "building-types/walls2/wallPlaneRules";
import type { Table } from "common/data/table";
import { executeDocBuildingGraphFilter } from "./buildingGraphFilterEngine";
import { executeFaceNodeProjection } from "./wallPlaneFilterExecution";

export function evaluateWallFaceRuleMatches(
  rule: WallFaceRuleEntity,
  catalogFilter: DocWallPlaneFilter | undefined,
  building: BuildingGraphFilterBuilding,
  precomputedCatalogMatches?: ReadonlyMap<string, RowForSource["FACE_NODE"]>,
): Map<string, RowForSource["FACE_NODE"]> | undefined {
  if (!wallFaceRuleHasConstraint(rule)) {
    return undefined;
  }

  const hasFilter = rule.filterId !== undefined && catalogFilter !== undefined;
  const hasInstance = instanceGraphHasPredicates(rule.instanceFilterGraphs, rule.instanceFilterGraphId);

  if (hasFilter && !hasInstance && precomputedCatalogMatches) {
    return new Map(precomputedCatalogMatches);
  }

  if (hasFilter && hasInstance && precomputedCatalogMatches) {
    const instanceMatches = executeInstanceFilterMatches(rule, building);
    return intersectFaceNodeMatches(precomputedCatalogMatches, instanceMatches);
  }

  if (!hasFilter && hasInstance) {
    return executeInstanceFilterMatches(rule, building);
  }

  const catalogGraph = hasFilter ? catalogFilter.filterGraphs[catalogFilter.filterGraphId] : undefined;
  const instanceGraph =
    hasInstance && rule.instanceFilterGraphs && rule.instanceFilterGraphId
      ? rule.instanceFilterGraphs[rule.instanceFilterGraphId]
      : undefined;
  const composed = composeFilterGraphsAnd(catalogGraph, instanceGraph);
  const filterGraphs =
    catalogFilter !== undefined ? { ...catalogFilter.filterGraphs, ...composed.filterGraphs } : composed.filterGraphs;

  return executeDocBuildingGraphFilter("FACE_NODE", filterGraphs, composed.filterGraphId, building, rule.id);
}

export function* executeWallFaceRule(
  rule: WallFaceRuleEntity,
  catalogFilters: Table<DocWallPlaneFilter>,
  building: BuildingGraphFilterBuilding,
  wallPlaneFilterMatches?: WallPlaneFilterMatches,
): Iterable<BuildingGraphFaceSideHandle> {
  if (!isWallFaceRule(rule) || !wallFaceRuleHasConstraint(rule)) {
    return;
  }

  const catalogFilter = rule.filterId ? catalogFilters[rule.filterId] : undefined;
  const hasFilter = rule.filterId !== undefined && catalogFilter !== undefined;
  const precomputed = hasFilter && rule.filterId ? wallPlaneFilterMatches?.[rule.filterId] : undefined;
  const precomputedCatalogMatches = precomputed?.targetType === "FACE_NODE" ? precomputed.matches : undefined;
  const matches = evaluateWallFaceRuleMatches(rule, catalogFilter, building, precomputedCatalogMatches);
  if (!matches) {
    return;
  }

  const subFilter = resolveWallFaceRuleSubFilter(rule, catalogFilter);
  const filterGraphs = {
    ...(catalogFilter?.filterGraphs ?? {}),
    ...(rule.instanceFilterGraphs ?? {}),
  };

  for (const handle of executeFaceNodeProjection(
    {
      direction: rule.direction,
      subFilter,
      filterGraphs,
      operationId: rule.id,
    },
    matches,
    building,
  )) {
    yield handle;
  }
}

function resolveWallFaceRuleSubFilter(
  rule: WallFaceRuleEntity,
  catalogFilter: DocWallPlaneFilter | undefined,
): DocFaceNodeSubFilter {
  if (rule.subFilter !== undefined && rule.subFilter.kind !== "UNDEFINED") {
    return rule.subFilter;
  }
  return catalogFilter?.subFilter ?? { kind: "UNDEFINED" };
}

function executeInstanceFilterMatches(
  rule: WallFaceRuleEntity,
  building: BuildingGraphFilterBuilding,
): Map<string, RowForSource["FACE_NODE"]> {
  return executeDocBuildingGraphFilter(
    "FACE_NODE",
    rule.instanceFilterGraphs,
    rule.instanceFilterGraphId,
    building,
    `${rule.id}#instance`,
  );
}

function intersectFaceNodeMatches(
  catalogMatches: ReadonlyMap<string, RowForSource["FACE_NODE"]>,
  instanceMatches: ReadonlyMap<string, RowForSource["FACE_NODE"]>,
): Map<string, RowForSource["FACE_NODE"]> {
  const result = new Map<string, RowForSource["FACE_NODE"]>();
  if (instanceMatches.size <= catalogMatches.size) {
    for (const [key] of instanceMatches) {
      if (catalogMatches.has(key)) {
        const catalogRow = catalogMatches.get(key);
        if (catalogRow) {
          result.set(key, catalogRow);
        }
      }
    }
    return result;
  }

  for (const [key, row] of catalogMatches) {
    if (instanceMatches.has(key)) {
      result.set(key, row);
    }
  }
  return result;
}
